/*
 * DEV-1272: Include Easify Product Options add-on prices (e.g. shirt print)
 * in the add-to-cart button price.
 *
 * Easify shows the total price (product + selected add-ons) by cloning the
 * product price element into `.tpo_product_price` inside the price block and
 * hiding the original. Broadcast's button price ([data-product-price]) is
 * calculated from the variant only, so the page showed two different prices.
 *
 * This extends product-info's updateButtonPrice() so the add-on amount shown
 * by Easify is added to the button price, and re-runs it whenever Easify
 * updates the price block. Without Easify (or with no paid option selected)
 * the theme's original behaviour is unchanged.
 */
(function () {
  const EASIFY_PRICE_SELECTOR = '.price__regular .tpo_product_price';
  const observed = new WeakSet();

  // Converts a formatted price ("899,00 kr", "1.498 kr", "$12.50") to cents.
  function parseCents(text) {
    const match = (text || '').match(/\d[\d.,'\s]*/);
    if (!match) return null;

    const token = match[0].trim();
    const value = parseInt(token.replace(/\D/g, ''), 10);
    if (isNaN(value)) return null;

    return /[.,]\d{2}$/.test(token) ? value : value * 100;
  }

  function getPriceBlock(productInfo) {
    return productInfo.querySelector(`#Price-${productInfo.dataset.section}`);
  }

  function getAddonPrice(productInfo, variant) {
    const easifyPrice = getPriceBlock(productInfo)?.querySelector(EASIFY_PRICE_SELECTOR);
    if (!easifyPrice) return 0;

    const totalPrice = parseCents(easifyPrice.textContent);
    if (totalPrice === null) return 0;

    return Math.max(totalPrice - (variant.price || 0), 0);
  }

  function applyAddonPrice(productInfo) {
    const buttonPrice = productInfo.querySelector('[data-product-price]');
    const variant = productInfo.getSelectedVariant(productInfo);
    if (!buttonPrice || !variant) return;

    const addonPrice = getAddonPrice(productInfo, variant);
    if (!addonPrice) return;

    const quantityInput = productInfo.quantityInput;
    const quantity = (quantityInput && parseInt(quantityInput.value || '1', 10)) || 1;
    const price = variant.price || 0;
    const compareAtPrice = variant.compare_at_price || 0;
    const formatMoney = (cents) => window.theme.formatMoney(cents, theme.moneyFormat);

    let html = formatMoney((price + addonPrice) * quantity);
    if (compareAtPrice > price) {
      html += ` <s>${formatMoney((compareAtPrice + addonPrice) * quantity)}</s>`;
    }
    buttonPrice.innerHTML = html;
  }

  function observePriceBlock(productInfo) {
    const priceBlock = getPriceBlock(productInfo);
    if (!priceBlock || observed.has(priceBlock) || !productInfo.querySelector('[data-product-price]')) return;
    observed.add(priceBlock);

    let frame = null;
    new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => productInfo.updateButtonPrice());
    }).observe(priceBlock, {childList: true, subtree: true, characterData: true});
  }

  customElements.whenDefined('product-info').then(() => {
    const proto = customElements.get('product-info').prototype;
    if (proto.easifyAddonPricePatched || typeof proto.updateButtonPrice !== 'function') return;
    proto.easifyAddonPricePatched = true;

    const updateButtonPrice = proto.updateButtonPrice;
    proto.updateButtonPrice = function () {
      updateButtonPrice.call(this);
      applyAddonPrice(this);
    };

    document.querySelectorAll('product-info').forEach(observePriceBlock);
    document.addEventListener('theme:product-info:loaded', (event) => observePriceBlock(event.target));
  });
})();
